import { Kpi as ApiKpi, KpiAnnotation as ApiKpiAnnotation, KpiEntry as ApiKpiEntry } from "@/api";
import { Paths } from "@/routes/paths";
import { parsePersonForTurboUi } from "@/models/people";
import { type SpaceKpisPageTypes as SpaceKpisPage, kpiDateFromIso as fromIsoDate } from "turboui";

export type { Kpi } from "@/api";
export {
  useAddKpiAnnotation,
  useCreateKpi,
  useDeleteKpi,
  useDeleteKpiAnnotation,
  useDeleteKpiEntry,
  useEditKpi,
  useEditKpiAnnotation,
  useEditKpiEntry,
  useLogKpiEntry,
} from "./kpiLifecycle";

// Map the API KPI shape onto the presentational turboui shape. Date-only
// `period` / annotation values arrive as `YYYY-MM-DD` and are parsed as local
// calendar days so the chart axis is not shifted by UTC midnight.
export function parseKpiForTurboUi(paths: Paths, kpi: ApiKpi): SpaceKpisPage.Kpi {
  return {
    id: kpi.id,
    name: kpi.name,
    description: kpi.description ? JSON.parse(kpi.description) : null,
    unit: kpi.unit,
    cadence: kpi.cadence as SpaceKpisPage.Cadence,
    champion: parsePersonForTurboUi(paths, kpi.champion),
    insertedAt: kpi.insertedAt ? new Date(kpi.insertedAt) : new Date(),
    link: paths.spaceKpiPath(kpi.spaceId, kpi.id),
    latestEntry: kpi.latestEntry ? parseKpiEntryForTurboUi(paths, kpi.latestEntry) : null,
    entries: (kpi.entries ?? []).map((entry) => parseKpiEntryForTurboUi(paths, entry)),
    annotations: (kpi.annotations ?? []).map((annotation) => parseKpiAnnotationForTurboUi(paths, annotation)),
  };
}

function parseKpiEntryForTurboUi(paths: Paths, entry: ApiKpiEntry): SpaceKpisPage.KpiEntry {
  return {
    id: entry.id,
    value: entry.value,
    recordedAt: fromIsoDate(entry.period),
    recordedBy: parsePersonForTurboUi(paths, entry.recordedBy),
    commentsCount: entry.commentsCount ?? 0,
    edits: (entry.edits ?? []).map((edit) => ({
      id: edit.id,
      previousValue: edit.previousValue,
      previousPeriod: fromIsoDate(edit.previousPeriod),
      editedBy: parsePersonForTurboUi(paths, edit.editedBy),
      editedAt: edit.insertedAt ? new Date(edit.insertedAt) : new Date(),
    })),
  };
}

function parseKpiAnnotationForTurboUi(paths: Paths, annotation: ApiKpiAnnotation): SpaceKpisPage.KpiAnnotation {
  return {
    id: annotation.id,
    date: fromIsoDate(annotation.date),
    title: annotation.title,
    createdBy: parsePersonForTurboUi(paths, annotation.createdBy),
  };
}
